import { clamp } from "../../../lib/Util";
import { DAYS_IN_WEEK, SHIFTS_PER_DAY } from "../../../lib/Constants";

/**
 * ZoomMenu - Service functions and constants for zooming the Gantt chart.
 */

const ZOOM_STEP_PERCENT = 5;
const DEFAULT_NORMALIZED_RANGE = 100;

type ZoomContext = {
    minDayWidth: number;
    planningDetailsWidth: number;
};

type ZoomRange = {
    minValue: number;
    maxValue: number;
};

const fitDaysWidth = (visibleDays: number, planningDetailsWidth: number, minDayWidth: number): number => {
    return Math.max(minDayWidth, planningDetailsWidth / visibleDays);
};

const getZoomRange = ({ minDayWidth, planningDetailsWidth }: ZoomContext): ZoomRange => {
    return {
        minValue: minDayWidth,
        maxValue: fitDaysWidth(1 / 3, planningDetailsWidth, minDayWidth),
    };
};

const getZoomFactor = ({ minValue, maxValue }: ZoomRange): number => {
    const normalizedStep = ZOOM_STEP_PERCENT / DEFAULT_NORMALIZED_RANGE;
    return Math.exp((Math.log(maxValue) - Math.log(minValue)) * normalizedStep);
};

const zoomIn = (currentZoom: number, zoomRange: ZoomRange): number => {
    return clamp(currentZoom * getZoomFactor(zoomRange), zoomRange.minValue, zoomRange.maxValue);
};

const zoomOut = (currentZoom: number, zoomRange: ZoomRange): number => {
    return clamp(currentZoom / getZoomFactor(zoomRange), zoomRange.minValue, zoomRange.maxValue);
};

const constrainDayWidth = (dayWidth: number, zoomRange: ZoomRange): number => {
    return clamp(dayWidth, zoomRange.minValue, zoomRange.maxValue);
};

const zoomToMonth = ({ minDayWidth }: ZoomContext): number => {
    return minDayWidth;
};

const zoomToVisibleDays = ({ planningDetailsWidth, minDayWidth }: ZoomContext, visibleDays: number): number => {
    return fitDaysWidth(visibleDays, planningDetailsWidth, minDayWidth);
};

const zoomToWeek = ({ planningDetailsWidth, minDayWidth }: ZoomContext): number => {
    return zoomToVisibleDays({ planningDetailsWidth, minDayWidth }, DAYS_IN_WEEK);
};

const zoomToDay = ({ planningDetailsWidth, minDayWidth }: ZoomContext): number => {
    return zoomToVisibleDays({ planningDetailsWidth, minDayWidth }, 1);
};

const zoomToShift = ({ planningDetailsWidth, minDayWidth }: ZoomContext): number => {
    return zoomToVisibleDays({ planningDetailsWidth, minDayWidth }, 1 / SHIFTS_PER_DAY);
};

export const ZoomMenu = {
    ZOOM_STEP_PERCENT,
    getZoomRange,
    constrainDayWidth,
    zoomIn,
    zoomOut,
    zoomToMonth,
    zoomToWeek,
    zoomToDay,
    zoomToShift,
};
